import React, { useEffect, useImperativeHandle, useState } from 'react'

import { IQuestionComponentProps, IQuestionComponentRef, QuestionViewFrame } from '../types'
import { IOpenEndedResponseValidation, OpenEndedQuestionModelTypes, OpenEndedResponse } from './types'

import useTimeline from '../../modules/timeline/useTimeline'
import useSession from '../../session/useSession'
import QuestionFrame from '../_common/QuestionFrame/QuestionFrame'
import OpenEndedView from './View'
import { doInnerPost } from '@aytm/app/swidget/base/modules/client/doPost'

const trimZipCode = (val: string) => val.trim().replace(/\r?\n|\r/g, '')

const OpenEndedQuestion = React.memo(
  React.forwardRef<IQuestionComponentRef, IQuestionComponentProps<OpenEndedQuestionModelTypes>>(({ model }, ref) => {
    const [[response, responseValidation, frame], setResponse] = useState<
      [OpenEndedResponse, IOpenEndedResponseValidation, QuestionViewFrame]
    >([model.response, model.responseValidation, model.frame])
    const [showValidation, setShowValidation] = useState(false)

    const session = useSession()
    const timeline = useTimeline()

    useEffect(() => {
      session.enableNext(responseValidation.valid)
    }, [session, responseValidation.valid])

    useImperativeHandle<IQuestionComponentRef, IQuestionComponentRef>(ref, () => {
      return {
        tryNext() {
          if (model.responseValidation.valid || session.skipValidation) {
            return false
          } else {
            session.enableNext(false)
            return true
          }
        },
        tryPrev() {
          return false
        },
        showValidation() {
          setShowValidation(true)
        }
      }
    }, [model, session])

    const responseUpdate = (response: OpenEndedResponse) => {
      model.response = response
      setResponse([model.response, model.responseValidation, model.frame])
      if (model.schema.validation_url)
        void (async () =>
          await doInnerPost(model.schema.validation_url, {
            id: model.schema.id,
            text: trimZipCode(response.comment)
          })
            .then(validateResponse => {
              model.response.comment = trimZipCode(response.comment)
              model.responseValidation.valid = validateResponse?.ok
              setResponse([model.response, model.responseValidation, model.frame])
            })
            .catch(() => {
              model.responseValidation.valid = false
              setResponse([model.response, model.responseValidation, model.frame])
            }))()
    }

    return (
      <QuestionFrame model={model}>
        <OpenEndedView
          schema={model.schema}
          response={response}
          responseValidation={responseValidation}
          showValidation={showValidation}
          onUpdateResponse={responseUpdate}
          frame={frame}
          timeline={timeline}
        />
      </QuestionFrame>
    )
  }),
  (prevProps, nextProps) => prevProps.model === nextProps.model
)

OpenEndedQuestion.displayName = 'OpenEndedQuestion'

export default OpenEndedQuestion
